'use strict';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import AppLink from './AppLink';
import { runtime } from '../inc/Runtime';

const isExternal = (url = '') => {
    return /^https?:\/\//i.test(url) || url.startsWith('mailto:') || url.startsWith('tel:');
};

const getSafeSvgMarkup = (value = '') => {
    const rawMarkup = String(value || '').trim();
    const svgStart = rawMarkup.search(/<svg[\s>]/i);
    const svgEnd = rawMarkup.search(/<\/svg>/i);
    const markup = svgStart >= 0
        ? rawMarkup.slice(svgStart, svgEnd >= svgStart ? svgEnd + 6 : undefined).trim()
        : '';

    if(!markup || !/^<svg[\s>]/i.test(markup)){
        return '';
    }

    return markup
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
        .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
        .replace(/\s(href|xlink:href)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, '');
};

const styleVariables = (item = {}) => {
    return Object.fromEntries(Object.entries({
        '--zero-un-global-icon-frame-width': item.frameWidth,
        '--zero-un-global-icon-frame-aspect-ratio': item.frameAspectRatio,
        '--zero-un-global-icon-frame-radius': item.frameBorderRadius,
        '--zero-un-global-icon-frame-bg': item.backgroundColor,
        '--zero-un-global-icon-frame-border': item.borderColor,
        '--zero-un-global-icon-color': item.iconColor,
        '--zero-un-global-icon-hover-frame-bg': item.hoverBackgroundColor,
        '--zero-un-global-icon-hover-frame-border': item.hoverBorderColor,
        '--zero-un-global-icon-hover-color': item.hoverIconColor,
        '--zero-un-global-icon-width': item.iconWidth,
    }).filter(([, value]) => value));
};

const GlobalIconItem = ({ item = {}, index = 0 }) => {
    const icon = getSafeSvgMarkup(item.iconSvg);

    if(!icon){
        return null;
    }

    const label = item.label || `Icône globale ${index + 1}`;
    const url = String(item.url || '').trim();
    const target = item.target === '_blank' ? '_blank' : undefined;
    const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
    const className = [
        'zero-un-global-icons__item',
        url ? 'is-clickable' : '',
    ].filter(Boolean).join(' ');
    const content = (
        <span
            className="zero-un-global-icons__svg"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: icon }}
        />
    );
    const props = {
        className,
        style: styleVariables(item),
        'aria-label': label,
    };

    if(!url){
        return (
            <span {...props} role="img">
                {content}
            </span>
        );
    }

    if(isExternal(url)){
        return (
            <a href={url} target={target} rel={rel} {...props}>
                {content}
            </a>
        );
    }

    return (
        <AppLink to={url} target={target} rel={rel} {...props}>
            {content}
        </AppLink>
    );
};

const GlobalIcons = () => {
    const [hasHeroQuickLinks, setHasHeroQuickLinks] = useState(false);
    const config = runtime.system?.zeroUn?.globalIcons || {};
    const items = Array.isArray(config.items) ? config.items : [];

    useEffect(() => {
        if(typeof document === 'undefined'){
            return undefined;
        }

        let rafId = 0;
        const updateHeroQuickLinksPresence = () => {
            if(rafId){
                window.cancelAnimationFrame(rafId);
            }

            rafId = window.requestAnimationFrame(() => {
                rafId = 0;
                setHasHeroQuickLinks(Boolean(document.querySelector('.zero-un-section--hero-hero_boxes .zero-un-hero-boxes')));
            });
        };
        const observer = new MutationObserver(updateHeroQuickLinksPresence);
        const target = document.getElementById('pageContent') || document.getElementById('app') || document.body;

        updateHeroQuickLinksPresence();
        window.addEventListener('zero-un-route-change', updateHeroQuickLinksPresence);
        observer.observe(target, {
            childList: true,
            subtree: true,
        });

        return () => {
            if(rafId){
                window.cancelAnimationFrame(rafId);
            }

            window.removeEventListener('zero-un-route-change', updateHeroQuickLinksPresence);
            observer.disconnect();
        };
    }, []);

    if(!items.length){
        return null;
    }

    const position = config.position === 'right_bottom' ? 'right-bottom' : 'right-center';

    const mountNode = typeof document !== 'undefined' ? document.body : null;

    if(!mountNode){
        return null;
    }

    return createPortal(
        <aside
            className={[
                'zero-un-global-icons',
                `zero-un-global-icons--${position}`,
                hasHeroQuickLinks ? 'has-hero-quick-links' : '',
            ].filter(Boolean).join(' ')}
            aria-label="Icônes globales"
        >
            {items.map((item, index) => (
                <GlobalIconItem
                    key={`${item.label || 'global-icon'}-${index}`}
                    item={item}
                    index={index}
                />
            ))}
        </aside>,
        mountNode
    );
};

export default GlobalIcons;
